import React from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { format, isValid } from "date-fns";
import {
    detectChartGranularity,
    formatChartTooltipDate,
    formatChartAxisDate,
} from "src/utils/chart-date-formatter";
import styled from "styled-components";
import calculateInterval from "src/components/calculate-tick-interval";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { Avatar, Chip, Stack } from "@mui/material";
import { resolveObjPathString } from "src/utils/resolve-path";
import ChartColors from "src/components/chart-colors";
import getRandomColor from "src/components/random-color-generator";
import timeSplitter from "src/utils/time-splitter";
import { resolveName } from "src/utils/templating";
import { DataTypesNames } from "src/components/template/constants";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
`;

const TooltipContentText = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const TooltipContent = ({ name, value, type, separator, color, xValue }) => {
    if (type === "chip")
        return <Chip avatar={<Avatar>{value}</Avatar>} size="small" label={name} />;
    if (type === DataTypesNames.duration)
        return (
            <Stack direction="row" spacing={0.5}>
                <TooltipContentText color={color}>{[name]} : </TooltipContentText>
                <TooltipContentText>
                    {`${timeSplitter(value, "variable-accuracy")} ${timeSplitter(value, "text")}`}
                </TooltipContentText>
            </Stack>
        );
    if (type === DataTypesNames.dow) {
        return (
            <Stack direction="row" spacing={0.5}>
                <TooltipContentText color={color}>
                    {name}
                    {isValid(new Date(xValue)) && format(new Date(xValue), "EEEE")}:
                </TooltipContentText>
                <TooltipContentText>{value}</TooltipContentText>
            </Stack>
        );
    }
    if (type === DataTypesNames.arrayToJoin) {
        if (value.length === 0 || !separator) return null;
        return (
            <Stack direction="row" spacing={0.5}>
                <TooltipContentText color={color}>{[name]} : </TooltipContentText>
                <TooltipContentText>{`${value.join(separator)}`}</TooltipContentText>
            </Stack>
        );
    }
    return (
        <Stack direction="row" spacing={0.5}>
            <TooltipContentText color={color}>{[name]} : </TooltipContentText>
            <TooltipContentText>{value}</TooltipContentText>
        </Stack>
    );
};

const CustomTooltip = ({ active, payload, tooltipValues, yValues, xValues, granularity }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer role="status" aria-live="assertive">
                <TooltipTitle>{`${
                    isValid(new Date(payload[0].payload.datetime)) &&
                    formatChartTooltipDate(payload[0].payload.datetime, granularity)
                }`}</TooltipTitle>
                {payload.map((entry) => {
                    const yValue = yValues[entry.dataKey];
                    if (!yValue) return null;
                    return (
                        <TooltipContent
                            name={yValue.name}
                            value={entry.value}
                            type={yValue.type}
                            separator={yValue.separator}
                            color={entry.color}
                            xValue={entry.payload[xValues[0].dataKey]}
                        />
                    );
                })}

                {tooltipValues && tooltipValues.length > 0 && (
                    <>
                        <div style={{ paddingTop: "1rem" }} />
                        {tooltipValues.map((value) => (
                            <TooltipContent
                                name={value.name}
                                value={payload[0].payload[value.dataKey]}
                                type={value.type}
                                separator={value.separator}
                                xValue={payload[0].payload[xValues[0].dataKey]}
                            />
                        ))}
                    </>
                )}
            </TooltipContainer>
        );
    }
    return null;
};

const TemplateBarChart = ({ data, parameters, context }) => {
    const [interval, setInterval] = React.useState(0);

    React.useEffect(() => {
        setInterval(calculateInterval({ data }));
    }, []);

    const handleColors = (index) => {
        return ChartColors[index] || getRandomColor();
    };

    const transform = () => {
        const transformedParameters = {
            yValues: {},
            xValues: [],
        };

        let fillCounter = 0;

        const granularity = detectChartGranularity(data);

        const transformedData = (data || []).map((d) => {
            const dataPoint = {};
            if (
                parameters.yValues.length === 1 &&
                parameters.yValues[0].propertyPath?.startsWith("[*]")
            ) {
                const [, propPath] = parameters.yValues[0].propertyPath.split("[*].");
                const path = resolveName(propPath, context);
                Object.keys(d)
                    .filter((key) => !parameters.xValues.find((xValue) => xValue.dataKey === key))
                    .forEach((key) => {
                        dataPoint[key] = resolveObjPathString(d[key], path);
                        transformedParameters.yValues[key] = {
                            ...parameters.yValues[0],
                            dataKey: key,
                            name: key,
                        };
                    });
            } else {
                parameters.yValues.forEach((yValue) => {
                    if (yValue.propertyPath?.includes(".")) {
                        const match = yValue.propertyPath.match(/\[([a-z]*)\]/);
                        if (match && match[1]) {
                            const nameProp = match[1];
                            const [iterationProp, ...otherProps] = yValue.propertyPath.split(
                                `[${nameProp}].`
                            );
                            const path = otherProps.join(".");
                            d[iterationProp].forEach((dValue) => {
                                const key = `${dValue[nameProp]}.${yValue.dataKey}`;
                                if (!transformedParameters.yValues[key]) {
                                    transformedParameters.yValues[key] = {
                                        ...yValue,
                                        name: `${dValue[nameProp]}${
                                            yValue.name ? ` ${yValue.name}` : ""
                                        }`,
                                        dataKey: key,
                                        fill: yValue.fill || handleColors(fillCounter),
                                    };
                                    fillCounter += 1;
                                }
                                if (!dataPoint[dValue[nameProp]]) {
                                    dataPoint[dValue[nameProp]] = {};
                                }
                                dataPoint[dValue[nameProp]][yValue.dataKey] = resolveObjPathString(
                                    dValue,
                                    path
                                );
                            });
                        } else {
                            transformedParameters.yValues[yValue.dataKey] = { ...yValue };
                            dataPoint[yValue.dataKey] = resolveObjPathString(
                                d,
                                yValue.propertyPath
                            );
                        }
                    } else {
                        transformedParameters.yValues[yValue.dataKey] = { ...yValue };
                        dataPoint[yValue.dataKey] = d[yValue.dataKey];
                    }
                });
            }
            parameters.xValues.forEach((xValue) => {
                dataPoint[xValue.dataKey] = d[xValue.dataKey];
                if (xValue.type === "datetime") {
                    dataPoint.date =
                        isValid(new Date(d[xValue.dataKey])) &&
                        formatChartAxisDate(d[xValue.dataKey], granularity);
                }
            });
            (parameters.tooltipValues || []).forEach((tooltipValue) => {
                if (tooltipValue.propertyPath?.includes(".")) {
                    dataPoint[tooltipValue.dataKey] = resolveObjPathString(
                        d,
                        tooltipValue.propertyPath
                    );
                } else {
                    dataPoint[tooltipValue.dataKey] = d[tooltipValue.dataKey];
                }
                // transformedParameters.yValues[tooltipValue.dataKey] = { ...tooltipValue };
            });
            return dataPoint;
        });
        return {
            transformedData,
            transformedParameters,
            granularity,
        };
    };

    const { transformedData, transformedParameters, granularity } = React.useMemo(transform, [
        JSON.stringify(data),
        JSON.stringify(parameters),
        JSON.stringify(context),
    ]);

    if (!transformedData || transformedData.length === 0)
        return <NoDataPlaceholder variant="bar" />;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                accessibilityLayer
                width="100%"
                height="100%"
                barGap="-10%"
                data={transformedData}
                margin={{
                    top: 5,
                    right: 20,
                    bottom: 5,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                {parameters.xValues &&
                    parameters.xValues.map((xValue) => (
                        <XAxis
                            dataKey={xValue.type === "datetime" ? "date" : xValue.dataKey}
                            interval={interval}
                            tick={{
                                fill: "#EAF0F4",
                                opacity: "0.3",
                                fontSize: "12px",
                                fontWeight: "300",
                            }}
                        />
                    ))}

                <Tooltip
                    cursor={{ opacity: "0.2" }}
                    content={
                        <CustomTooltip
                            tooltipValues={parameters.tooltipValues}
                            yValues={transformedParameters.yValues}
                            xValues={parameters.xValues}
                            granularity={granularity}
                        />
                    }
                />
                {Object.values(transformedParameters.yValues) &&
                    Object.values(transformedParameters.yValues).map((yValue, index) => (
                        <Bar
                            dataKey={yValue.dataKey}
                            name={yValue.name}
                            maxBarSize={yValue.maxBarSize}
                            barSize={yValue.barSize}
                            fill={yValue.fill || handleColors(index)}
                            stackId={yValue.stackId}
                            unit={yValue.unit}
                        />
                    ))}
            </BarChart>
        </ResponsiveContainer>
    );
};

export default TemplateBarChart;
